import React from 'react'
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../../components/breadcrumb'
import { cn } from '../../lib/utils'

export interface UIBreadcrumbProps extends React.ComponentProps<typeof Breadcrumb> {
  variant?: 'default' | 'minimal' | 'enclosed'
  size?: 'sm' | 'md' | 'lg'
}

export interface UIBreadcrumbListProps extends React.ComponentProps<typeof BreadcrumbList> {
  spacing?: 'tight' | 'normal' | 'loose'
}

export interface UIBreadcrumbItemProps extends React.ComponentProps<typeof BreadcrumbItem> {
  isCurrentPage?: boolean
}

export interface UIBreadcrumbLinkProps extends React.ComponentProps<typeof BreadcrumbLink> {
  variant?: 'default' | 'subtle' | 'prominent'
}

export interface UIBreadcrumbPageProps extends React.ComponentProps<typeof BreadcrumbPage> {
  variant?: 'default' | 'emphasized'
}

/**
 * Enhanced Breadcrumb component following design system best practices
 * Provides consistent navigation breadcrumb styling and behavior
 */
export const UIBreadcrumb = React.forwardRef<
  React.ComponentRef<typeof Breadcrumb>,
  UIBreadcrumbProps
>(({ className, variant = 'default', size = 'md', ...props }, ref) => {
  return (
    <Breadcrumb
      ref={ref}
      className={cn(
        // Base styles
        'w-full',

        // Variant styles
        {
          '': variant === 'default',
          'px-3 py-2 bg-muted/30 rounded-lg': variant === 'enclosed',
          'opacity-80': variant === 'minimal',
        },

        // Size styles
        {
          'text-xs': size === 'sm',
          'text-sm': size === 'md',
          'text-base': size === 'lg',
        },

        className
      )}
      {...props}
    />
  )
})

export const UIBreadcrumbList = React.forwardRef<
  React.ComponentRef<typeof BreadcrumbList>,
  UIBreadcrumbListProps
>(({ className, spacing = 'normal', ...props }, ref) => {
  return (
    <BreadcrumbList
      ref={ref}
      className={cn(
        {
          'gap-1': spacing === 'tight',
          'gap-2': spacing === 'normal',
          'gap-3': spacing === 'loose',
        },
        className
      )}
      {...props}
    />
  )
})

export const UIBreadcrumbItem = React.forwardRef<
  React.ComponentRef<typeof BreadcrumbItem>,
  UIBreadcrumbItemProps
>(({ className, ...props }, ref) => {
  return (
    <BreadcrumbItem
      ref={ref}
      className={cn(
        'transition-colors',
        className
      )}
      {...props}
    />
  )
})

export const UIBreadcrumbLink = React.forwardRef<
  React.ComponentRef<typeof BreadcrumbLink>,
  UIBreadcrumbLinkProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <BreadcrumbLink
      ref={ref}
      className={cn(
        // Base styles
        'transition-colors',

        // Variant styles
        {
          'text-muted-foreground hover:text-foreground': variant === 'default',
          'text-muted-foreground/70 hover:text-muted-foreground': variant === 'subtle',
          'text-foreground font-medium hover:text-primary': variant === 'prominent',
        },

        className
      )}
      {...props}
    />
  )
})

export const UIBreadcrumbPage = React.forwardRef<
  React.ComponentRef<typeof BreadcrumbPage>,
  UIBreadcrumbPageProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <BreadcrumbPage
      ref={ref}
      className={cn(
        {
          'text-foreground': variant === 'default',
          'text-foreground font-semibold': variant === 'emphasized',
        },
        className
      )}
      {...props}
    />
  )
})

// Re-export other breadcrumb components with consistent naming
export const UIBreadcrumbSeparator = BreadcrumbSeparator
export const UIBreadcrumbEllipsis = BreadcrumbEllipsis

UIBreadcrumb.displayName = "UIBreadcrumb"
UIBreadcrumbList.displayName = "UIBreadcrumbList"
UIBreadcrumbItem.displayName = "UIBreadcrumbItem"
UIBreadcrumbLink.displayName = "UIBreadcrumbLink"
UIBreadcrumbPage.displayName = "UIBreadcrumbPage"